import { isEndEffector } from '@sb/integrations/utils';
import type { ArmJointPositions } from '@sb/motion-planning';
import { isEndEffectorTCP } from '@sb/motion-planning';
import type { Space } from '@sb/routine-runner';
import type { RoutineRunnerHandle } from '@sbrc/services';

/**
 * The vizbot can surface `jointAngles` as a non-array value (a typed array,
 * or a plain object with numeric keys `0..5` produced by a state-clone round
 * trip). Either form serializes over the wire as `{"0": ...}` instead of
 * `[...]`, which fails the `Space.Position` zod tuple schema when the client
 * reconstructs the routine — so `convertRoutineResponse` silently drops any
 * space item containing such a position.
 *
 * Normalize to a plain 6-tuple here so the payload round-trips correctly.
 */
function toPlainArray(angles: ArmJointPositions): ArmJointPositions {
  if (Array.isArray(angles)) {
    return angles;
  }

  const anyAngles = angles as unknown as ArrayLike<number> &
    Record<number, number>;

  // Typed arrays and array-likes with a numeric `length` work via Array.from.
  if (typeof anyAngles.length === 'number') {
    return Array.from(anyAngles) as unknown as ArmJointPositions;
  }

  // Plain object with numeric keys — read 0..5 directly.
  return [
    anyAngles[0],
    anyAngles[1],
    anyAngles[2],
    anyAngles[3],
    anyAngles[4],
    anyAngles[5],
  ] as unknown as ArmJointPositions;
}

export const getRobotPosition = (
  routineRunnerHandle: RoutineRunnerHandle,
): Space.Position | null => {
  const routineRunnerState = routineRunnerHandle.getState();

  if (!routineRunnerState) {
    return null;
  }

  const tcpOption = routineRunnerState.kinematicState.tcpOffsetOption;

  // If tcpOption is for an end effector, find the active end effector and save its ID
  let gripperID: string | undefined;

  if (tcpOption && isEndEffectorTCP(tcpOption)) {
    const activeEndEffector = routineRunnerState.equipmentState.find((device) =>
      isEndEffector(device.kind),
    );

    if (activeEndEffector) {
      gripperID = activeEndEffector.id;
    }
  }

  return {
    jointAngles: toPlainArray(routineRunnerState.kinematicState.jointAngles),
    pose: routineRunnerState.kinematicState.tooltipPoint,
    tcpOption,
    gripperID,
    wristPose:
      routineRunnerState.kinematicState.tcpOffsetOption === 'wrist'
        ? undefined
        : routineRunnerState.kinematicState.wristPose,
  };
};
